import { ShopBox as ShopBoxType } from '@@partner:types:app'
import { GQL_ID } from '@@partner:types:graphql'

import React, { useState } from 'react'
import { groupWith } from 'rambda'

import { Button, Col, FlexboxGrid, Form, Grid, Input, InputNumber, Row, SelectPicker, Panel } from 'rsuite'

import { commonFieldChange, convertToInt } from '@@partner:helpers/util'
import BoxLabelPrinter from './BoxLabelPrinter'

import './ShopBoxes.scss'


type EditableShopBox = Omit<ShopBoxType, 'id'>

type NewBox = Omit<EditableShopBox, 'number'> & {
  column: number
  row: string // Теперь буква (A, B, C...)
  place: number
}

type NewRow = {
  letter: string
  columns: number
  places: number
  special_conditions: 'refrigerated' | 'frozen' | 'heated' | ''
}

type RecreateRow = {
  letter: string
  special_conditions: 'refrigerated' | 'frozen' | 'heated' | ''
}

const specialConditionsOptions = [
  { label: 'Обычные', value: '' },
  { label: 'Холодильные', value: 'refrigerated' },
  { label: 'Морозильные', value: 'frozen' },
  { label: 'Подогреваемые', value: 'heated' }
]

// Утилитарные функции для работы с новой системой нумерации
const getRowLetterByNumber = (rowNumber: number): string => {
  return String.fromCharCode(65 + rowNumber - 1) // A=1, B=2, C=3...
}

const getRowNumberByLetter = (letter: string): number => {
  return letter.charCodeAt(0) - 64 // A=1, B=2, C=3...
}

const generateBoxNumber = (rowLetter: string, column: number, place: number): number => {
  const rowNumber = getRowNumberByLetter(rowLetter)
  const paddedColumn = column.toString().padStart(2, '0')
  const paddedPlace = place.toString().padStart(2, '0')
  return parseInt(`${rowNumber}${paddedColumn}${paddedPlace}`)
}

const parseBoxNumber = (number: number): { rowLetter: string, column: number, place: number } => {
  const str = number.toString().padStart(5, '0')
  const rowNumber = parseInt(str[0])
  const column = parseInt(str.slice(1, 3))
  const place = parseInt(str.slice(3, 5))
  
  return {
    rowLetter: getRowLetterByNumber(rowNumber),
    column,
    place
  }
}

type Props = {
  shopId: GQL_ID
  boxes: EditableShopBox[]
  onChange: (boxes: EditableShopBox[]) => void
}


export default function ShopBoxes ({ shopId, boxes, onChange }: Props) {
  const [ newBox, setNewBox ] = useState<NewBox>({
    name: '',
    column: 1,
    row: 'A',
    place: 1,
    shop_id: shopId
  })
  const [ newRow, setNewRow ] = useState<NewRow>({
    letter: 'A',
    columns: 1,
    places: 1,
    special_conditions: ''
  })
  const [ recreateInfo, setRecreateInfo ] = useState({
    rows: [] as RecreateRow[]
  })
  const [ recreate, setRecreate ] = useState(false)
  const [ addRowMode, setAddRowMode ] = useState(false)

  const sortedBoxes = boxes.sort((a, b) => a.number - b.number)

  const handleChangeNewBox = commonFieldChange(setNewBox)
  const handleChangeNewRow = commonFieldChange(setNewRow)

  const handleRowSelect = (value: string) => {
    if (value === 'NEW') {
      // Определяем следующую доступную букву
      const usedLetters = existingRows
      const nextLetter = String.fromCharCode(65 + usedLetters.length)
      setNewBox(prev => ({ ...prev, row: nextLetter }))
    }
    else {
      setNewBox(prev => ({ ...prev, row: value }))
    }
  }

  const handleChangeBox = (number: number, newBoxInfo: EditableShopBox) => {
    const newBoxes = sortedBoxes.map((box) => {
      if (number === box.number) {
        return newBoxInfo
      }
      return box
    })
    onChange(newBoxes)
  }

  const handleDeleteBox = (number: number) => {
    const newBoxes = sortedBoxes.filter((box) => box.number !== number)
    onChange(newBoxes)
  }

  const handleDeleteRow = (rowLetter: string) => {
    const rowNumber = getRowNumberByLetter(rowLetter)
    const newBoxes = sortedBoxes.filter((box) => {
      const boxRowNumber = Math.floor(box.number / 10000)
      return boxRowNumber !== rowNumber
    })
    onChange(newBoxes)
  }

  const handleAddBox = () => {
    const boxNumber = generateBoxNumber(newBox.row, newBox.column, newBox.place)
    const specialConditions = boxes.find(box => {
      const parsed = parseBoxNumber(box.number)
      return parsed.rowLetter === newBox.row
    })?.special_conditions || ''

    const newBoxes = [
      ...boxes,
      {
        name: newBox.name || `${newBox.row}${newBox.column.toString().padStart(2, '0')}${newBox.place.toString().padStart(2, '0')}`,
        number: boxNumber,
        shop_id: shopId,
        special_conditions: specialConditions as any
      }
    ]
    onChange(newBoxes)
  }

  const handleAddRow = () => {
    let newBoxes = [ ...boxes ]

    for (let column = 1; column <= newRow.columns; column++) {
      for (let place = 1; place <= newRow.places; place++) {
        const boxNumber = generateBoxNumber(newRow.letter, column, place)
        newBoxes.push({
          name: `${newRow.letter}${column.toString().padStart(2, '0')}${place.toString().padStart(2, '0')}`,
          number: boxNumber,
          shop_id: shopId,
          special_conditions: newRow.special_conditions as any
        })
      }
    }
    
    onChange(newBoxes)
    setAddRowMode(false)
  }

  const handleRecreate = () => {
    let newBoxes: EditableShopBox[] = []

    recreateInfo.rows.forEach((rowInfo) => {
      for (let column = 1; column <= 10; column++) { // Фиксируем 10 колонок
        for (let place = 1; place <= 10; place++) { // Фиксируем 10 мест
          const boxNumber = generateBoxNumber(rowInfo.letter, column, place)
          newBoxes.push({
            name: `${rowInfo.letter}${column.toString().padStart(2, '0')}${place.toString().padStart(2, '0')}`,
            number: boxNumber,
            shop_id: shopId,
            special_conditions: rowInfo.special_conditions as any
          })
        }
      }
    })
    
    onChange(newBoxes)
    setRecreate(false)
  }

  const handleAddRecreateRow = () => {
    const nextLetter = String.fromCharCode(65 + recreateInfo.rows.length)
    setRecreateInfo(info => ({
      ...info,
      rows: [ ...info.rows, { letter: nextLetter, special_conditions: '' } ]
    }))
  }

  const handleRemoveRecreateRow = (index: number) => {
    setRecreateInfo(info => ({
      ...info,
      rows: info.rows.filter((_, i) => i !== index)
    }))
  }

  const handleUpdateRecreateRow = (index: number, rowData: Partial<RecreateRow>) => {
    setRecreateInfo(info => ({
      ...info,
      rows: info.rows.map((row, i) => i === index ? { ...row, ...rowData } : row)
    }))
  }

  const groupByRow = groupWith((a: EditableShopBox, b: EditableShopBox) => {
    const aRowNumber = Math.floor(a.number / 10000)
    const bRowNumber = Math.floor(b.number / 10000)
    return aRowNumber === bRowNumber
  }, sortedBoxes)

  // Группируем ячейки по рядам и колонкам для правильного отображения
  const groupBoxesByRowAndColumn = (boxes: EditableShopBox[]) => {
    const grouped: { [key: string]: { [key: number]: EditableShopBox[] } } = {}
    
    boxes.forEach(box => {
      const parsed = parseBoxNumber(box.number)
      const rowKey = parsed.rowLetter
      
      if (!grouped[rowKey]) {
        grouped[rowKey] = {}
      }
      if (!grouped[rowKey][parsed.column]) {
        grouped[rowKey][parsed.column] = []
      }
      
      grouped[rowKey][parsed.column].push(box)
    })
    
    // Сортируем места в каждой колонке
    Object.keys(grouped).forEach(rowKey => {
      Object.keys(grouped[rowKey]).forEach(columnKey => {
        const column = parseInt(columnKey)
        grouped[rowKey][column].sort((a, b) => {
          const parsedA = parseBoxNumber(a.number)
          const parsedB = parseBoxNumber(b.number)
          return parsedA.place - parsedB.place
        })
      })
    })
    
    return grouped
  }

  const groupedBoxes = groupBoxesByRowAndColumn(sortedBoxes)
  
  // Получаем уникальные ряды для отображения
  const existingRows = [ ...new Set(boxes.map(box => {
    const parsed = parseBoxNumber(box.number)
    return parsed.rowLetter
  })) ].sort()
  
  return (
    <div>
      <div className='ShopBoxes'>
        { Object.keys(groupedBoxes).sort().map(rowLetter => {
          const rowBoxes = groupedBoxes[rowLetter]
          const firstBox = Object.values(rowBoxes)[0]?.[0]
          const specialConditionsLabel = specialConditionsOptions.find(
            opt => opt.value === firstBox?.special_conditions
          )?.label || 'Обычные'
          
          return (
            <div key={`row-${rowLetter}`} className='ShopBoxes__ShopRow'>
              <div className='flex justify-between items-center mb-2'>
                <h4>Ряд {rowLetter} ({specialConditionsLabel})</h4>
                <Button
                  color='red'
                  appearance='ghost'
                  size='sm'
                  onClick={() => handleDeleteRow(rowLetter)}
                >
                  Удалить ряд
                </Button>
              </div>
              <div className='ShopBoxes__ColumnsGrid'>
                {Object.keys(rowBoxes).sort((a, b) => parseInt(a) - parseInt(b)).map(columnKey => {
                  const column = parseInt(columnKey)
                  const columnBoxes = rowBoxes[column]
                  
                  return (
                    <div key={`column-${column}`} className='ShopBoxes__Column'>
                      <div className='ShopBoxes__ColumnHeader'>Колонка {column}</div>
                      <div className='ShopBoxes__Places'>
                        {columnBoxes.map(box => (
                          <SingleShopBox
                            key={`box:${box.number}`}
                            boxes={boxes}
                            box={box}
                            onChange={handleChangeBox}
                            onDelete={handleDeleteBox}
                          />
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
      <span className="block my-2">Добавить ячейку</span>
      <Grid fluid>
        <Col xs={6}>
          <span className="text-xs">Название</span>
          <Input
            value={newBox.name}
            onChange={handleChangeNewBox('name')}
          />
        </Col>
        <Col xs={6}>
          <span className="text-xs">Ряд</span>
          <SelectPicker
            data={[ ...existingRows.map(letter => ({ label: `Ряд ${letter}`, value: letter })), 
              { label: 'Новый ряд...', value: 'NEW' } ]}
            value={newBox.row}
            onChange={(value) => handleRowSelect(value || 'A')}
            searchable={false}
            cleanable={false}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={6}>
          <span className="text-xs">Колонка</span>
          <InputNumber
            scrollable={false}
            value={newBox.column}
            step={1}
            min={1}
            max={99}
            onChange={(value) => handleChangeNewBox('column')(convertToInt(value || 1))}
          />
        </Col>
        <Col xs={6}>
          <span className="text-xs">Место</span>
          <InputNumber
            scrollable={false}
            value={newBox.place}
            step={1}
            min={1}
            max={99}
            onChange={(value) => handleChangeNewBox('place')(convertToInt(value || 1))}
          />
        </Col>
        <div className='mt-5'>
          <Button
            color='green'
            appearance='ghost'
            onClick={handleAddBox}
            className='mt-5'
          >
            Добавить ячейку
          </Button>
        </div>
      </Grid>
      
      <Button 
        appearance='ghost'
        color='blue'
        onClick={() => setAddRowMode(true)}
        className='mt-5'
      >
        Добавить ряд
      </Button>
      
      { addRowMode && (
        <Grid fluid className='mt-5'>
          <Row>
            <span className='text-md'>Создание нового ряда</span>
          </Row>
          <Col xs={6}>
            <span className="text-xs">Буква ряда</span>
            <Input
              value={newRow.letter}
              onChange={handleChangeNewRow('letter')}
              maxLength={1}
            />
          </Col>
          <Col xs={6}>
            <span className="text-xs">Условия хранения</span>
            <SelectPicker
              data={specialConditionsOptions}
              value={newRow.special_conditions}
              onChange={(value) => handleChangeNewRow('special_conditions')((value || '') as any)}
              searchable={false}
              cleanable={false}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={6}>
            <span className="text-xs">Количество колонок</span>
            <InputNumber
              scrollable={false}
              value={newRow.columns}
              step={1}
              min={1}
              max={99}
              onChange={(value) => handleChangeNewRow('columns')(convertToInt(value || 1))}
            />
          </Col>
          <Col xs={6}>
            <span className="text-xs">Количество мест</span>
            <InputNumber
              scrollable={false}
              value={newRow.places}
              step={1}
              min={1}
              max={99}
              onChange={(value) => handleChangeNewRow('places')(convertToInt(value || 1))}
            />
          </Col>
          <div className='mt-5'>
            <Button
              color='green'
              appearance='ghost'
              onClick={handleAddRow}
              className='mr-2'
            >
              Создать ряд
            </Button>
            <Button
              appearance='ghost'
              onClick={() => setAddRowMode(false)}
            >
              Отмена
            </Button>
          </div>
        </Grid>
      )}
      <Button 
        appearance='ghost'
        color='red'
        onClick={() => setRecreate(true)}
        className='mt-10'
      >
        Пересоздать
      </Button>
      { recreate && (
        <Grid fluid className='mt-5'>
          <Row>
            <span className='text-md'>Это действие перепишет все ячейки</span>
          </Row>
          <Row className='mt-3'>
            <span className='text-sm'>Добавьте ряды для создания:</span>
          </Row>
          
          {recreateInfo.rows.map((row, index) => (
            <Row key={index} className='mt-3 border p-3'>
              <Col xs={6}>
                <span className="text-xs">Буква ряда</span>
                <Input
                  value={row.letter}
                  onChange={(value) => handleUpdateRecreateRow(index, { letter: value })}
                  maxLength={1}
                />
              </Col>
              <Col xs={12}>
                <span className="text-xs">Условия хранения</span>
                <SelectPicker
                  data={specialConditionsOptions}
                  value={row.special_conditions}
                  onChange={(value) => handleUpdateRecreateRow(index, { special_conditions: (value || '') as any })}
                  searchable={false}
                  cleanable={false}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={6} className='mt-2'>
                <Button
                  color='red'
                  appearance='ghost'
                  size='sm'
                  onClick={() => handleRemoveRecreateRow(index)}
                >
                  Удалить ряд
                </Button>
              </Col>
            </Row>
          ))}
          
          <div className='mt-5'>
            <Button
              color='blue'
              appearance='ghost'
              onClick={handleAddRecreateRow}
              className='mr-2'
            >
              Добавить ряд
            </Button>
            <Button
              color='green'
              appearance='ghost'
              onClick={handleRecreate}
              className='mr-2'
              disabled={recreateInfo.rows.length === 0}
            >
              Пересоздать
            </Button>
            <Button
              appearance='ghost'
              onClick={() => setRecreate(false)}
            >
              Отмена
            </Button>
          </div>
        </Grid>
      )}
      
      {/* Печать этикеток */}
      {boxes.length > 0 && (
        <Panel className='mt-10' header="Печать QR-этикеток">
          <BoxLabelPrinter
            boxes={boxes.map(box => ({ ...box, id: box.shop_id }))}
            shopId={shopId}
          />
        </Panel>
      )}
    </div>
  )
}

type ShopBoxProps = {
  boxes: EditableShopBox[]
  box: EditableShopBox
  onChange: (number: number, value: EditableShopBox) => void
  onDelete: (number: number) => void
}

function SingleShopBox ({ 
  boxes, 
  box, 
  onChange,
  onDelete
}: ShopBoxProps) {
  const [ isEditing, setIsEditing ] = useState(false) 
  const [ boxInfo, setBoxInfo ] = useState(box)
  const parsed = parseBoxNumber(box.number)

  const handleFieldChange = commonFieldChange(setBoxInfo)
  
  const handleSave = () => {
    onChange(box.number, boxInfo)
    setIsEditing(false)
  }
  const handleDelete = () => {
    onDelete(box.number)
    setIsEditing(false)
  }

  if (isEditing) {
    return (
      <Form 
        className='ShopBoxes__ShopBox'
        formValue={boxInfo}
      >
        <FlexboxGrid justify='center'>
          <Input
            value={boxInfo.name}
            onChange={handleFieldChange('name')}
          />
          <Button
            color='green'
            appearance='ghost'
            onClick={handleSave}
            className='my-2 ml-2 mr-2'
          >
            Сохранить
          </Button>
          <Button
            color='red'
            appearance='ghost'
            onClick={handleDelete}
            className='my-2 ml-2 mr-2'
          >
            Удалить
          </Button>
        </FlexboxGrid>
      </Form>
    )
  }

  return (
    <div className='ShopBoxes__ShopBox'>
      <div className={`special-conditions-indicator special-conditions-indicator--${box.special_conditions || 'normal'}`}></div>
      <div className='text-center'>
        <div className='font-bold'>{boxInfo.name}</div>
        <div className='text-xs text-gray-500'>
          {parsed.rowLetter}{parsed.column.toString().padStart(2, '0')}{parsed.place.toString().padStart(2, '0')}
        </div>
        <div className='text-xs'>№{box.number}</div>
      </div>
      <Button onClick={() => setIsEditing(true)} size='sm' className='mt-2'>
        Изменить
      </Button>
    </div>
  )
}